// My books › Library copies › Library history (Phase 6.4 Part 2, step 2; designed Oct 20, boards
// 8 and 9; settled with Kristi Oct 31: open to start, By book first, this year's summary, read
// only). Every library step for good: the year's summary, By book (each book folded to a summary
// line, newest change first) or By date (by month), filter chips Loans · Holds · Didn't finish
// (several at once, none on = every step: consistency pass R3, Kristi Nov 2).
// No database calls here (tests/logic/my-books-history.test.ts).

import { EVENT_NAMES, TEAL_STEPS, monthHeading } from '../book/model';
import { loanFormat, type LoanFact } from './library';

export interface HistEvent {
  id: string;
  loanId: string;
  bookId: string;
  label: number;
  title: string;
  format: string;
  listen: boolean;
  kind: string;
  on: string | null;
  dueOn: string | null;
  percent: number | null;
  createdAt: string;
}

/** A kind of step to show; none on means every step ("All" goes: R3 P2). */
export type HistKind = 'loans' | 'holds' | 'didnt_finish';
export const HIST_KINDS: { key: HistKind; label: string }[] = [
  { key: 'loans', label: 'Loans' },
  { key: 'holds', label: 'Holds' },
  { key: 'didnt_finish', label: 'Didn’t finish' },
];

const LOAN_KINDS = new Set(['borrowed', 'renewed', 'started_reading', 'finished', 'didnt_finish', 'returned', 'returned_unread', 'switched_to_own_copy']);
const HOLD_KINDS = new Set(['hold_placed', 'new_expected_date', 'ready', 'deliver_later', 'let_go', 'cancelled']);

const isKind = (e: Pick<HistEvent, 'kind'>, k: HistKind) => (k === 'loans' ? LOAN_KINDS.has(e.kind) : k === 'holds' ? HOLD_KINDS.has(e.kind) : e.kind === 'didnt_finish');

/** Whether a step shows under the chips that are on (none on: every step). */
export function keepEvent(e: Pick<HistEvent, 'kind'>, kinds: HistKind[]): boolean {
  return !kinds.length || kinds.some((k) => isKind(e, k));
}

/** A remembered choice, kept to the kinds there are (the old single "all" becomes none on). */
export const cleanKinds = (v: unknown): HistKind[] => HIST_KINDS.map((k) => k.key).filter((k) => (Array.isArray(v) ? v.includes(k) : v === k));

/** Each chip's [n]: loans, holds placed, and times you ran out before finishing (as the year's summary counts them, for all years). */
export function kindCounts(events: Pick<HistEvent, 'kind' | 'loanId'>[]): Record<HistKind, number> {
  const trips = (k: HistKind) => new Set(events.filter((e) => isKind(e, k)).map((e) => e.loanId)).size;
  return { loans: trips('loans'), holds: trips('holds'), didnt_finish: events.filter((e) => e.kind === 'didnt_finish').length };
}

/** A step's name and pill tone, as on the book page's Library history. */
export const stepName = (kind: string) => EVENT_NAMES[kind] ?? kind;
export const stepTone = (kind: string): 'teal' | 'quiet' => (TEAL_STEPS.has(kind) ? 'teal' : 'quiet');

/** What else a step says ("due Nov 19", "at 46%"). */
export function stepDetail(e: Pick<HistEvent, 'kind' | 'dueOn' | 'percent'>, dayText: (d: string) => string): string {
  if ((e.kind === 'borrowed' || e.kind === 'renewed') && e.dueOn) return `due ${dayText(e.dueOn)}`;
  if (e.kind === 'didnt_finish' && e.percent !== null) return `at ${Math.round(e.percent)}%`;
  return '';
}

/** The library's own order, for steps recorded at the same moment (the sheet import wrote a trip's steps together). */
const STEP_ORDER = ['hold_placed', 'new_expected_date', 'ready', 'deliver_later', 'borrowed', 'started_reading', 'renewed', 'finished', 'didnt_finish', 'switched_to_own_copy', 'returned', 'returned_unread', 'let_go', 'cancelled', 'other'];
const stepRank = (k: string) => (STEP_ORDER.includes(k) ? STEP_ORDER.indexOf(k) : STEP_ORDER.length);

/**
 * Newest first (Kristi, Oct 31: what's happening now at the top): by day, then by when it was
 * recorded (each step keeps that time; it isn't shown), then the library's own order reversed.
 */
export const newest = (a: HistEvent, b: HistEvent) =>
  (b.on ?? '').localeCompare(a.on ?? '') || b.createdAt.localeCompare(a.createdAt) || stepRank(b.kind) - stepRank(a.kind);

const times = (n: number, what: string) => `${what} ${n === 1 ? 'once' : n === 2 ? '2 times' : `${n} times`}`;

/** "2026 so far: 5 loans · 1 finished · 1 ran out before I finished · 2 returned unread · 14 holds placed". */
export function yearSummary(events: HistEvent[], today: string): string {
  const year = today.slice(0, 4);
  const mine = events.filter((e) => e.on?.startsWith(year));
  const n = (k: string) => mine.filter((e) => e.kind === k).length;
  const parts: string[] = [];
  const loans = n('borrowed');
  if (loans) parts.push(loans === 1 ? '1 loan' : `${loans} loans`);
  if (n('finished')) parts.push(`${n('finished')} finished`);
  if (n('didnt_finish')) parts.push(`${n('didnt_finish')} ran out before I finished`);
  if (n('returned_unread')) parts.push(`${n('returned_unread')} returned unread`);
  if (n('hold_placed')) parts.push(n('hold_placed') === 1 ? '1 hold placed' : `${n('hold_placed')} holds placed`);
  return parts.length ? `${year} so far: ${parts.join(' · ')}` : `Nothing from the library yet in ${year}.`;
}

export interface BookHistory {
  bookId: string;
  label: number;
  title: string;
  /** "Kindle and Libby audio". */
  formats: string;
  /** More than one copy: each step names its copy. */
  several: boolean;
  /** "borrowed 2 times · ran out once · Now: borrowed, due Nov 19". */
  summary: string;
  /** Its steps, newest first (Kristi, Oct 31). */
  steps: HistEvent[];
  latest: string;
}

/** Where a book's library copies stand now ("Now: borrowed, due Nov 19"); empty when nothing is going. The book page says it the same way (lib/libraryStates nowLine). */
export function nowLine(loans: LoanFact[], dayText: (d: string) => string): string {
  const b = loans.filter((l) => l.step === 'borrowed').sort((x, y) => (x.dueOn ?? '').localeCompare(y.dueOn ?? ''))[0];
  if (b) return `Now: borrowed${b.dueOn ? `, due ${dayText(b.dueOn)}` : ''}`;
  if (loans.some((l) => l.step === 'ready')) return 'Now: ready to borrow';
  if (loans.some((l) => l.step === 'on_hold')) return 'Now: on hold';
  return '';
}

/** By book: one entry per book, newest change first. */
export function byBook(events: HistEvent[], loans: LoanFact[], filter: HistKind[], dayText: (d: string) => string): BookHistory[] {
  const kept = events.filter((e) => keepEvent(e, filter));
  const groups = new Map<string, HistEvent[]>();
  for (const e of kept) groups.set(e.bookId, [...(groups.get(e.bookId) ?? []), e]);
  const out: BookHistory[] = [];
  for (const [bookId, list] of groups) {
    const all = events.filter((e) => e.bookId === bookId);
    const count = (k: string) => all.filter((e) => e.kind === k).length;
    const bits: string[] = [];
    if (count('borrowed')) bits.push(times(count('borrowed'), 'borrowed'));
    if (count('renewed')) bits.push(times(count('renewed'), 'renewed'));
    if (count('didnt_finish')) bits.push(times(count('didnt_finish'), 'ran out'));
    if (count('returned_unread')) bits.push(times(count('returned_unread'), 'returned unread'));
    if (count('let_go') + count('cancelled')) bits.push(times(count('let_go') + count('cancelled'), 'let go'));
    if (!bits.length && count('hold_placed')) bits.push(count('hold_placed') === 1 ? 'hold placed' : `${count('hold_placed')} holds placed`);
    const now = nowLine(loans.filter((l) => l.bookId === bookId), dayText);
    if (now) bits.push(now);
    const formats = [...new Set(all.map((e) => loanFormat(e)))];
    const steps = [...list].sort(newest);
    out.push({
      bookId,
      label: list[0]!.label,
      title: list[0]!.title,
      several: formats.length > 1,
      formats: formats.length > 1 ? `${formats.slice(0, -1).join(', ')} and ${formats.at(-1)}` : (formats[0] ?? ''),
      summary: bits.join(' · '),
      steps,
      latest: steps[0]!.on ?? '',
    });
  }
  return out.sort((a, b) => b.latest.localeCompare(a.latest) || a.title.localeCompare(b.title));
}

export interface MonthHistory {
  /** "2026-10". */
  month: string;
  steps: HistEvent[];
}

/** By date: by month, newest first; steps with no date at the end under "Date not recorded". */
export function byMonth(events: HistEvent[], filter: HistKind[]): MonthHistory[] {
  const kept = events.filter((e) => keepEvent(e, filter)).sort(newest);
  const groups = new Map<string, HistEvent[]>();
  for (const e of kept) {
    const m = e.on ? e.on.slice(0, 7) : '';
    groups.set(m, [...(groups.get(m) ?? []), e]);
  }
  return [...groups.entries()].map(([month, steps]) => ({ month, steps })).sort((a, b) => (a.month === '' ? 1 : b.month === '' ? -1 : b.month.localeCompare(a.month)));
}

/** A month's heading through the one formatter ("October 2026"). */
export const monthName = (m: string) => (m ? monthHeading(`${m}-01`) : 'Date not recorded');
